import React, { useRef, useState, useEffect } from 'react'
import { StyleProp, StyleSheet, TextInput, TextInputProps, View, ViewStyle } from 'react-native'
import { useTheme } from '@react-navigation/native'
import { Theme } from '@theme'
import { useLanguage } from '../../context/LanguageContext'
import { Icon } from '../atoms'

interface SearchBarProps extends TextInputProps {
  containerStyle?: StyleProp<ViewStyle>,
  hasMaxWidth?: boolean
  // The text shown in the empty box instead of « Rechercher ». The standard
  // `placeholder` prop has always been ignored here, and several screens pass
  // one: reading it would change the text of all of them.
  placeholderText?: string
}

export const SearchBar = ({ containerStyle, hasMaxWidth, placeholderText, ...props }: SearchBarProps): React.ReactElement => {
  const { t } = useLanguage()

  const theme = useTheme()
  const styles = getStyle(theme)

  const textInputRef = useRef<TextInput>(null)

  const [inputValue, setInputValue] = useState(props.value || '')

  // Synchroniser le state interne avec la prop value
  useEffect(() => {
    setInputValue(props.value || '')
  }, [props.value])

  const onClear = () => {
    textInputRef.current?.clear()
    setInputValue('')
    props.onChangeText?.('')
  }

  const onChangeText = (text: string) => {
    setInputValue(text)
    props.onChangeText?.(text)
  }

  return (
    <View style={[styles.container, containerStyle, hasMaxWidth && styles.maxWidth]}>
      <Icon name="search" color={theme.colors.lightGrey} size={theme.iconSize.small} />
      <TextInput
        {...props}
        ref={textInputRef}
        autoFocus={props.autoFocus}
        value={inputValue}
        onChangeText={onChangeText}
        returnKeyType="search"
        placeholderTextColor={theme.colors.lightGrey}
        placeholder={placeholderText ?? t('search.placeholder')}
        autoCorrect={false}
        style={[styles.input, props.style]}
      />
      {!!inputValue && <Icon name="cross" color={theme.colors.lightGrey} onPress={onClear} />}
    </View>
  )
}

const getStyle = (theme: Theme) =>
  StyleSheet.create({
    container: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingHorizontal: theme.spacing.sm,
      borderRadius: theme.border.jumbo * 2,
      borderWidth: 1,
      borderColor: theme.colors.lightGrey,
    },
    maxWidth: {
      maxWidth: 400,
    },
    input: {
      flex: 1,
      minWidth: 0,
      paddingLeft: theme.spacing.md,
      paddingRight: theme.spacing.md,
      paddingVertical: theme.spacing.sm,
      fontSize: theme.fontSize.medium,
      color: theme.colors.primary,
      textAlign: 'left',
    },
  })
